<template>
  <v-dialog
    v-model="dialog"
    max-width="960"
    scrollable
    content-class="device-offline-devices-dialog"
  >
    <v-card class="device-offline-devices-dialog__card">
      <div
        class="device-offline-devices-dialog__hero"
        :style="heroStyle"
      >
        <div class="device-offline-devices-dialog__hero-main">
          <div class="device-offline-devices-dialog__icon-wrap">
            <v-icon color="white">{{ typeMeta.icon }}</v-icon>
          </div>
          <div>
            <div class="device-offline-devices-dialog__title">
              {{ typeMeta.label }} «Не в сети»
            </div>
            <div class="device-offline-devices-dialog__subtitle">
              Список установленных устройств без сети
            </div>
          </div>
        </div>
        <v-btn
          icon
          dark
          class="device-offline-devices-dialog__close"
          @click="dialog = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="device-offline-devices-dialog__meta">
        <v-chip
          small
          label
          class="device-offline-devices-dialog__chip"
          color="blue-grey lighten-5"
          text-color="blue-grey darken-2"
        >
          <v-icon left x-small>mdi-calendar</v-icon>
          {{ formattedDate }}
        </v-chip>
        <v-chip
          small
          label
          class="device-offline-devices-dialog__chip"
          :color="typeMeta.chipColor"
          :text-color="typeMeta.chipText"
        >
          <v-icon left x-small>mdi-counter</v-icon>
          {{ items.length }} {{ devicesWord }}
        </v-chip>
        <v-chip
          v-if="isLive"
          small
          label
          class="device-offline-devices-dialog__chip"
          color="green lighten-4"
          text-color="green darken-3"
        >
          <v-icon left x-small>mdi-access-point</v-icon>
          Актуально сейчас
        </v-chip>
      </div>

      <v-card-text class="device-offline-devices-dialog__body">
        <v-text-field
          v-model="search"
          dense
          outlined
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          placeholder="Поиск по ID, адресу, району или компании"
          class="device-offline-devices-dialog__search mb-4"
        />

        <v-data-table
          :headers="headers"
          :items="filteredItems"
          :loading="loading"
          :items-per-page="15"
          :footer-props="{
            'items-per-page-options': [15, 30, 50, -1],
            showFirstLastPage: true,
          }"
          class="device-offline-devices-dialog__table elevation-0"
          loading-text="Загрузка списка..."
          no-data-text="По запросу ничего не найдено"
          no-results-text="По запросу ничего не найдено"
        >
          <template v-slot:item.id="{ item }">
            <router-link
              v-if="isDoorphoneType"
              :to="{ name: 'DetailDoorphone', params: { id: String(item.id) } }"
              class="device-offline-devices-dialog__id device-offline-devices-dialog__id--link"
              @click.native="onDoorphoneLinkClick"
            >
              #{{ item.id }}
            </router-link>
            <span v-else class="device-offline-devices-dialog__id">#{{ item.id }}</span>
          </template>
          <template v-slot:item.address="{ item }">
            <div class="device-offline-devices-dialog__cell">
              <v-icon x-small :color="typeMeta.color" class="mr-1">mdi-map-marker-outline</v-icon>
              <span>{{ item.address || '—' }}</span>
            </div>
          </template>
          <template v-slot:item.region="{ item }">
            <div class="device-offline-devices-dialog__cell">
              <v-icon x-small color="blue-grey" class="mr-1">mdi-map-outline</v-icon>
              <span>{{ item.region || '—' }}</span>
            </div>
          </template>
          <template v-slot:item.management_company="{ item }">
            <div class="device-offline-devices-dialog__cell">
              <v-icon x-small color="blue-grey" class="mr-1">mdi-domain</v-icon>
              <span>{{ item.management_company || '—' }}</span>
            </div>
          </template>
        </v-data-table>
      </v-card-text>

      <v-divider />

      <v-card-actions class="device-offline-devices-dialog__actions">
        <span class="device-offline-devices-dialog__footnote">
          Показано {{ filteredItems.length }} из {{ items.length }}
        </span>
        <v-spacer />
        <v-btn
          rounded
          class="text-none"
          color="primary"
          depressed
          @click="dialog = false"
        >
          Закрыть
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ruDateFormatMixin from '@/mixins/ruDateFormat';

const TYPE_META = {
  doorphones: {
    label: 'Домофоны',
    icon: 'mdi-doorbell-video',
    color: '#1976d2',
    gradient: 'linear-gradient(135deg, #0d47a1 0%, #1976d2 55%, #42a5f5 100%)',
    chipColor: 'blue lighten-5',
    chipText: 'blue darken-2',
  },
  barriers: {
    label: 'Шлагбаумы',
    icon: 'mdi-boom-gate',
    color: '#f57c00',
    gradient: 'linear-gradient(135deg, #e65100 0%, #fb8c00 55%, #ffb74d 100%)',
    chipColor: 'orange lighten-5',
    chipText: 'orange darken-3',
  },
  gates: {
    label: 'Калитки',
    icon: 'mdi-gate',
    color: '#43a047',
    gradient: 'linear-gradient(135deg, #2e7d32 0%, #43a047 55%, #81c784 100%)',
    chipColor: 'green lighten-5',
    chipText: 'green darken-3',
  },
};

export default {
  name: 'DeviceOfflineDailyDevicesDialog',
  mixins: [ruDateFormatMixin],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    date: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: () => [],
    },
    isLive: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      search: '',
      headers: [
        { text: 'ID', value: 'id', width: 90 },
        { text: 'Адрес', value: 'address' },
        { text: 'Район', value: 'region', width: 180 },
        { text: 'Компания собственника', value: 'management_company', width: 240 },
      ],
    };
  },
  computed: {
    dialog: {
      get () {
        return this.value;
      },
      set (val) {
        this.$emit('input', val);
      },
    },
    typeMeta () {
      return TYPE_META[this.type] || {
        label: 'Устройства',
        icon: 'mdi-devices',
        color: '#546e7a',
        gradient: 'linear-gradient(135deg, #455a64 0%, #78909c 100%)',
        chipColor: 'blue-grey lighten-5',
        chipText: 'blue-grey darken-2',
      };
    },
    heroStyle () {
      return {
        background: this.typeMeta.gradient,
      };
    },
    formattedDate () {
      return this.date ? this.ruDateFormat(this.date, true) : '';
    },
    devicesWord () {
      const count = this.items.length;
      const mod10 = count % 10;
      const mod100 = count % 100;
      if (mod10 === 1 && mod100 !== 11) return 'устройство';
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'устройства';
      return 'устройств';
    },
    isDoorphoneType () {
      return this.type === 'doorphones';
    },
    filteredItems () {
      const query = (this.search || '').trim().toLowerCase();
      if (!query) return this.items;

      return this.items.filter((item) => {
        const haystack = [
          item.id,
          item.address,
          item.region,
          item.management_company,
        ].map((value) => String(value || '').toLowerCase()).join(' ');

        return haystack.includes(query);
      });
    },
  },
  watch: {
    value (open) {
      if (!open) {
        this.search = '';
      }
    },
  },
  methods: {
    onDoorphoneLinkClick () {
      this.dialog = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.device-offline-devices-dialog__card {
  overflow: hidden;
  border-radius: 16px !important;
}

.device-offline-devices-dialog__hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 20px 18px;
  color: #fff;
}

.device-offline-devices-dialog__hero-main {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.device-offline-devices-dialog__icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.device-offline-devices-dialog__title {
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.2;
}

.device-offline-devices-dialog__subtitle {
  margin-top: 4px;
  font-size: 0.84rem;
  color: rgba(255, 255, 255, 0.86);
}

.device-offline-devices-dialog__close {
  margin-top: -4px;
}

.device-offline-devices-dialog__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 14px 20px 0;
}

.device-offline-devices-dialog__chip {
  font-weight: 500;
}

.device-offline-devices-dialog__body {
  padding-top: 16px !important;
}

.device-offline-devices-dialog__search ::v-deep .v-input__slot {
  min-height: 40px !important;
  border-radius: 10px;
}

.device-offline-devices-dialog__table {
  border: 1px solid #e3eaf3;
  border-radius: 12px;
  overflow: hidden;
}

.device-offline-devices-dialog__table ::v-deep thead th {
  background: #f5f9ff !important;
  color: #455a64 !important;
  font-size: 0.78rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.device-offline-devices-dialog__table ::v-deep tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
}

.device-offline-devices-dialog__id {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eceff1;
  font-family: monospace;
  font-size: 0.82rem;
  color: #455a64;
  font-weight: 600;
}

.device-offline-devices-dialog__id--link {
  text-decoration: none;
  color: #1565c0;
  background: #e3f2fd;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.device-offline-devices-dialog__id--link:hover {
  background: #bbdefb;
  color: #0d47a1;
}

.device-offline-devices-dialog__cell {
  display: flex;
  align-items: flex-start;
  gap: 2px;
  line-height: 1.35;
}

.device-offline-devices-dialog__actions {
  padding: 12px 20px 16px !important;
}

.device-offline-devices-dialog__footnote {
  font-size: 0.8rem;
  color: #90a4ae;
}
</style>
